import React, { useEffect, useState, useRef, useMemo } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import { FilterComponent } from "./filterComponent/FilterComponent";
import ContactSearchTable from "./contactTable/ContactSearchTable";
import CompanySearchTable from "./contactTable/CompanySearchTable";
import { dataSearchState, decodeListBuilderExtensionImportParam } from "./state";
import customListState from "./CustomList/state";
import CreateEditListPopup, { createEditListPopupState } from "./CustomList/createEditListPopup";
import PopupLoader from "./PopupLoader";
import { AiSearchBox, AiSearchNote } from "./AiSearchBox";
import { currentSearchState, locationAndCompanyDomainPayloadCreation } from "./searchState";
import InputTextBox from "Components/common/InputTextBox";
import Button from "Components/common/Button";
import makeApi, { URLS } from "Utils/apiURL";
import { toasterState } from "Components/common/toaster";
import Utils from "Utils/utils";
import { MXP_EVENT, PAGE_TYPE } from "Static/MixPanelEvents";
import { settingsStoreData } from "Stores/settingsData";
import copilotStore from "Stores/copilotStore";
import copilotDrawerStore from "Stores/copilotDrawerStore";
import deepResearchStore from "Stores/deepResearchData";
import { useCopilotContext } from "Pages/Dashboard/Copilot/agent/copilotContext";
import usePageFindings from "Pages/Dashboard/Copilot/agent/usePageFindings";
import listBuilderFindings from "./listBuilderFindings";
import searchEmptyFindings from "./searchEmptyFindings";
import SellerUrlPromptPopup from "Pages/Dashboard/settings/SellerProfiles/SellerUrlPromptPopup";
import { agentsData } from "Stores/agentsData";
import { AGENT_SEARCH_ACK, AGENT_SEARCH_UPDATE, openAgentSearchChannel } from "../agents/agentSearchChannel";

const SEARCH_UNSAVED_DWELL_MS = 60000;

const DataSearch = observer(props => {
  const history = useHistory();
  const [showIcpPopup, setShowIcpPopup] = useState(false);
  const [dwellReady, setDwellReady] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [showSavePopup, setShowSavePopup] = useState(false);
  const [editPopup, setEditPopup] = useState(false);
  const [saveLoader, setSaveLoader] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchId, setSearchId] = useState("");
  const [errorMessage, setErrorMessage] = useState(false);
  const [savedListOptions, setSavedListOptions] = useState([]);
  const [filterSliderOpen, setFilterSliderOpen] = useState(false);
  const inputBoxRef = useRef();
  const [icpSaving, setIcpSaving] = useState(false);

  // Industry display labels are used by the dropdown, chips and panels — load them up front.
  useEffect(() => {
    dataSearchState.loadIndustryLabels();
  }, []);

  useEffect(() => {
    deepResearchStore.fetchSellerProfiles();
  }, []);

  useEffect(() => {
    setDwellReady(false);
    const t = setTimeout(() => setDwellReady(true), SEARCH_UNSAVED_DWELL_MS);
    return () => clearTimeout(t);
  }, [dataSearchState.lastSearchedFilters]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sgLb = params.get("sgLb");
    if (sgLb) {
      const parsed = decodeListBuilderExtensionImportParam(sgLb);
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("sgLb");
        window.history.replaceState({}, "", url.pathname + url.search + url.hash);
      } catch (e) {
        /* ignore */
      }
      if (parsed && parsed.filters) {
        // Opened from an agent to build its ICP: remember which agent the search will be saved for.
        dataSearchState.setIcpAgent(parsed.icpAgent || null);
        // Opened from an agent draft to refine its search: offer to send the filters back to it.
        dataSearchState.setAgentSearch(parsed.agentSearch || null);
        dataSearchState.applyFiltersFromCompanyExtensionImport(parsed.filters, parsed.selected).then(() => {
          // A company search (e.g. an agent's drafted "find companies" search) opens on the Companies tab.
          if (parsed.tab === "companies") {
            dataSearchState.setActiveSearchTab("companies");
            dataSearchState.searchCompanyData();
          }
        });
      }
    } else {
      dataSearchState.loadPersistedFilters();
    }
    return () => {
      dataSearchState.setContactTableData({});
      dataSearchState.setRangeSelected("");
      dataSearchState.setShowInitialPage(true);
      customListState.setActiveList("");
    };
  }, []);

  // Sync discoverTableLoading (e.g. from auto-search on first filter) so table shows loading state
  useEffect(() => {
    setTableLoading(dataSearchState.discoverTableLoading);
  }, [dataSearchState.discoverTableLoading]);

  useEffect(() => {
    saveSearchConfirmationPopup();
  }, [editPopup]);

  const saveSearch = (savedpayload = {}) => saveSearchNamed(searchInput, savedpayload);

  const saveSearchNamed = (searchName, savedpayload = {}) =>
    makeApi({ url: URLS.saveSearchV2, method: "POST", data: { searchName, ...currentSearchState(savedpayload) } });



  // "Use in agent": send the current filters back to the agent draft that opened this tab. The draft
  // acknowledges; without an answer (its tab was closed or reloaded) the user is told to save instead.
  const [agentSearchSending, setAgentSearchSending] = useState(false);
  const sendToAgent = () => {
    const agentSearch = dataSearchState.agentSearch;
    const channel = openAgentSearchChannel();
    if (!agentSearch || !channel || agentSearchSending) return;
    setAgentSearchSending(true);
    const state = JSON.parse(JSON.stringify(currentSearchState(locationAndCompanyDomainPayloadCreation())));
    let answered = false;
    const done = () => { channel.close(); setAgentSearchSending(false); };
    const timer = setTimeout(() => {
      if (answered) return;
      done();
      toasterState.setToastMsg("The agent draft isn't open anymore. Save this search and pick it in the agent instead.", "failure");
    }, 2000);
    channel.onmessage = event => {
      const msg = event.data || {};
      if (msg.type !== AGENT_SEARCH_ACK || msg.token !== agentSearch.token) return;
      answered = true;
      clearTimeout(timer);
      done();
      dataSearchState.setAgentSearch(null);
      toasterState.setToastMsg(`Search updated in “${agentSearch.name}”`, "success");
      // Back to the agent tab. List Builder runs in a frame, so close the top window (allowed only for the tab the agent opened).
      (window.top || window).close();
    };
    channel.postMessage({ type: AGENT_SEARCH_UPDATE, token: agentSearch.token, state });
  };

  // "Save as ICP": save the current filters as a saved search and make it the agent's ICP.
  const saveAsIcp = async () => {
    const icpAgent = dataSearchState.icpAgent;
    if (!icpAgent || icpSaving) return;
    setIcpSaving(true);
    const extras = locationAndCompanyDomainPayloadCreation();
    const baseName = `${icpAgent.name || "Agent"} — ICP`;
    let response = null;
    for (const name of [baseName, `${baseName} (${new Date().toLocaleString()})`]) {
      response = await saveSearchNamed(name, extras);
      const status = response?.status || response?.response?.status;
      if (status !== 409) break; // a search with that name exists — retry once with a dated name
    }
    const savedId = response?.data?.id;
    const agent = savedId ? await agentsData.setIcpSavedSearch(icpAgent.id, savedId) : null;
    setIcpSaving(false);
    if (agent) {
      toasterState.setToastMsg("Saved as the agent's ICP", "success");
      dataSearchState.setIcpAgent(null);
      history.push(`/agents/${icpAgent.id}`);
    } else {
      toasterState.setToastMsg("Couldn't save the ICP search. Please try again.", "failure");
    }
  };

  const handleSavedSearch = async event => {
    setSaveLoader(true);
    let savedpayload = locationAndCompanyDomainPayloadCreation();
    let response = await saveSearch(savedpayload);
    if (response?.status === 200) {
      Utils.mixpanelTrack(MXP_EVENT.SAVE_SEARCH, {
        pageType: PAGE_TYPE.discover
      });
      toasterState.setToastMsg("Search saved succesfully", "success");
    } else {
      toasterState.setToastMsg(
        "save operation was unsuccessful,please try again later",
        "failure"
      );
    }
    setSaveLoader(false);
    setShowSavePopup(false);
  };

  const handleInputBoxEvent = event => {
    Utils.preventDefaultFn(event);
    if (Utils.isSubmitKeyAction(event)) {
      handleSavedSearch();
    }
  };

  const minimumSelectionMade =
    (dataSearchState?.selectedFilteredList &&
      Object.keys(dataSearchState?.selectedFilteredList).length === 0 &&
      dataSearchState?.filterComponentData &&
      Object.keys(dataSearchState?.filterComponentData).length <= 2) ||
    false;

  const inputProps = {
    name: showSavePopup ? "Save Search" : "Edit Search",
    id: showSavePopup ? "saveSearch" : "editsearch",
    placeHolder: showSavePopup ? "CMOs of $10M+ companies" : "Edit Search",
    type: "text",
    value: searchInput,
    change: (id, val) => {
      setErrorMessage("");
      setSearchInput(val);
    },
    cbk: e => {
      if (!checkDuplicateName()) {
        if (searchInput) {
          showSavePopup ? handleInputBoxEvent(e) : saveSearchAfterEditText(e);
        }
      } else {
        if (Utils.isSubmitKeyAction(e)) {
          setErrorMessage("Entered search already exists");
        }
      }
    },
    err: errorMessage,
    readOnly: false,
    inputBoxRef: inputBoxRef,
    className: `${searchInput ? "activeBorder" : ""}`
  };

  const btnProps = {
    id: showSavePopup ? "Saved Search Button" : "Edit Search Button",
    name: showSavePopup ? "savedSearchButton" : "editSearchButton",
    btnText: showSavePopup ? "Save Search" : "Edit search",
    type: "button",
    className: "saveButton",
    className: `saveButton ${saveLoader ? "loading" : ""}`,
    loader: saveLoader,
    click: e => {
      if (!checkDuplicateName()) {
        if (searchInput) {
          setErrorMessage("");
          showSavePopup ? handleSavedSearch(e) : editSavedSearch();
        }
      } else {
        setErrorMessage("Entered search already exists");
      }
    },
    disabled: !searchInput || (minimumSelectionMade && !editPopup)
  };

  const getSavedSearch = () => {
    const config = {
      url: URLS.getSavedSearchV2,
      method: "GET"
    };
    return makeApi(config);
  };

  const initialGetSearch = async () => {
    let response = await getSavedSearch();
    if (response?.status === 200) {
      const raw = response?.data;
      const list = Array.isArray(raw)
        ? raw
        : raw?.content ?? raw?.data ?? raw?.savedSearches ?? [];
      setSavedListOptions(Array.isArray(list) ? list : []);
    }
  };

  const checkDuplicateName = () => {
    if (Array.isArray(savedListOptions)) {
      return savedListOptions?.some(item => {
        return item?.searchName === searchInput;
      });
    }
  };

  const editSavedSearch = async () => {
    let response = await editApiCall();
    setEditPopup(false);
    if (response?.status === 200) {
      Utils.mixpanelTrack(MXP_EVENT.EDIT_SEARCH, {
        pageType: PAGE_TYPE.discover
      });
      toasterState.setToastMsg("saved search edited successfully", "success");
    } else {
      toasterState.setToastMsg("saved search edit was unsuccessful", "failure");
    }
  };

  const editApiCall = () => {
    const config = {
      url: URLS.editSearchV2,
      method: "POST",
      data: { id: searchId, name: searchInput }
    };
    return makeApi(config);
  };

  const saveSearchAfterEditText = e => {
    Utils.preventDefaultFn(e);
    if (Utils.isSubmitKeyAction(e)) {
      editSavedSearch();
    }
  };

  const editCallBack = (id = "", name = "") => {
    setErrorMessage("");
    setSearchInput(name);
    setSearchId(id);
    setEditPopup(true);
  };

  useEffect(() => {
    if (showSavePopup) {
      setSearchInput("");
      setErrorMessage("");
    }
  }, [showSavePopup]);

  useEffect(() => {
    if (showSavePopup || editPopup) {
      initialGetSearch();
    }
  }, [showSavePopup, editPopup]);

  const saveSearchConfirmationPopup = () => {
    let displayText = editPopup ? "Edit Search" : "Save Search";
    return (
      <section className="createSavedSearchPopupWrapper">
        <div className="card">
          <div className="popupHeader">
            <div className="title">{displayText}</div>
            <i
              className="material-icons commonPopupCloseIcon"
              onClick={() =>
                editPopup ? setEditPopup(false) : setShowSavePopup(false)
              }
            >
              close
            </i>
          </div>
          <InputTextBox {...inputProps} />
          <Button {...btnProps} />
        </div>
        <div
          className="common-popup-mask"
          onClick={() => setShowSavePopup(false)}
        />
      </section>
    );
  };

  const activeTab = dataSearchState.activeSearchTab;
  const [tabFading, setTabFading] = useState(false);

  const switchTab = tab => {
    if (tab === activeTab || tabFading) return;
    setTabFading(true);
    setTimeout(() => {
      dataSearchState.setActiveSearchTab(tab);
      setTabFading(false);
    }, 130);
  };

  useCopilotContext("list_builder", () => ({
    viewState: { tab: activeTab, filters: dataSearchState.lastSearchedFilters, total: dataSearchState.contactTableData?.totalContacts },
    selection: dataSearchState.selectedContactData?.length
      ? { type: "prospect", ids: dataSearchState.selectedContactData, total: dataSearchState.selectedContactData.length }
      : undefined
  }));

  const askCopilot = (text, { send = true } = {}) => {
    if (send) copilotStore.setPendingAutoSend(text);
    else copilotStore.setComposerDraft(text);
    copilotDrawerStore.openFor("list_builder", "");
  };

  const isShowingResults = dataSearchState.hasData || !dataSearchState.showInitialPage;

  const copilotFindings = useMemo(() => {
    if (!isShowingResults) {
      const hasSearched = dataSearchState.isFilterApplied || !!dataSearchState.lastSearchedFilters;
      return searchEmptyFindings({
        hasSearched,
        hasProfile: deepResearchStore.sellerProfiles.length > 0,
        savedLists: customListState.totalContactList || [],
        actions: {
          buildIcp: () => setShowIcpPopup(true),
          useIcp: () =>
            askCopilot(
              "Turn my seller profile into list-builder filters: titles, seniority, industries, company size, locations. Propose the filters; I will apply them."
            ),
          describe: () => askCopilot("Show me contacts who ", { send: false }),
          rerun: () => dataSearchState.applyFiltersAndSearch(),
          findSimilar: name =>
            askCopilot(`Find new prospects similar to my list "${name}" and propose the filters.`)
        }
      });
    }

    return listBuilderFindings({
      total: dataSearchState.contactTableData?.totalContacts,
      selected: dataSearchState.selectedContactData,
      selectedCount: (() => {
        const r = dataSearchState.rangeSelected || {};
        const lim = parseInt(r.limit, 10);
        return (r.type === "custom" || r.type === "bulk") && lim > 0
          ? lim
          : dataSearchState.selectedContactData?.length || 0;
      })(),
      credits: settingsStoreData.totalRemainingCredits,
      activeList: customListState.activeList,
      rows: dataSearchState.contactTableData?.contacts,
      dwellReady,
      actions: {
        loosen: () =>
          askCopilot(
            "My list-builder search returned nothing. Given my current filters, which one should I loosen first? Do not change anything."
          ),
        narrow: () =>
          askCopilot(
            "My list-builder search returned too many results. Given my current filters, which one should I add or tighten to narrow this down? Do not change anything."
          ),
        excludeExisting: () => askCopilot("Exclude contacts already in Salesgear from my current results."),
        saveToList: () => createEditListPopupState.setShowPopup(true)
      }
    });
  }, [
    isShowingResults,
    dataSearchState.lastSearchedFilters,
    dataSearchState.isFilterApplied,
    dataSearchState.contactTableData?.totalContacts,
    dataSearchState.contactTableData?.contacts,
    dataSearchState.selectedContactData,
    dataSearchState.rangeSelected,
    dataSearchState.hasData,
    dataSearchState.showInitialPage,
    settingsStoreData.totalRemainingCredits,
    customListState.activeList,
    customListState.totalContactList,
    deepResearchStore.sellerProfiles.length,
    dwellReady
  ]);
  usePageFindings(copilotFindings, "discover");

  return (
    <section className={`dataSearchV2Wrapper ${filterSliderOpen ? "filterSliderOpen" : ""}`}>
      <div className="dataSearchV2Filter">
        <FilterComponent
          setTableLoading={setTableLoading}
          setShowSavePopup={setShowSavePopup}
          saveSearchConfirmationPopup={saveSearchConfirmationPopup}
          setEditPopup={setEditPopup}
          setSearchId={setSearchId}
          searchInput={searchInput}
          editCallBack={editCallBack}
          minimumSelectionMade={minimumSelectionMade}
          savedListOptions={savedListOptions}
          setSavedListOptions={setSavedListOptions}
          onSliderOpenChange={setFilterSliderOpen}
          activeTab={activeTab}
        />
      </div>

      {(showSavePopup || editPopup) && saveSearchConfirmationPopup()}

      <div className="dataSearchTable">
        {dataSearchState.agentSearch && (
          <div className="dsIcpBanner">
            <i className="material-icons dsIcpBanner__icon">tune</i>
            <div className="dsIcpBanner__text">
              <b>Refining the search for “{dataSearchState.agentSearch.name}”.</b> Adjust the filters and check the
              results, then send them back to the agent.
            </div>
            <button type="button" className="dsIcpBanner__cancel" onClick={() => dataSearchState.setAgentSearch(null)} disabled={agentSearchSending}>
              Dismiss
            </button>
            <button type="button" className="dsIcpBanner__save" onClick={sendToAgent} disabled={agentSearchSending}>
              {agentSearchSending ? "Sending…" : "Use in agent"}
            </button>
          </div>
        )}
        {dataSearchState.icpAgent && (
          <div className="dsIcpBanner">
            <i className="material-icons dsIcpBanner__icon">track_changes</i>
            <div className="dsIcpBanner__text">
              <b>Building the ICP for “{dataSearchState.icpAgent.name}”.</b> Pre-filled from the seller profile — refine the
              filters and check the results, then save it as the agent's ICP.
            </div>
            <button type="button" className="dsIcpBanner__cancel" onClick={() => dataSearchState.setIcpAgent(null)} disabled={icpSaving}>
              Cancel
            </button>
            <button type="button" className="dsIcpBanner__save" onClick={saveAsIcp} disabled={icpSaving}>
              {icpSaving ? "Saving…" : "Save as ICP"}
            </button>
          </div>
        )}
        <div className="dsTabBar">
          <button
            className={`dsTab ${activeTab === "contacts" ? "dsTab--active" : ""}`}
            onClick={() => switchTab("contacts")}
          >
            <i className="material-icons">people</i>
            Contacts
          </button>
          <button
            className={`dsTab ${activeTab === "companies" ? "dsTab--active" : ""}`}
            onClick={() => switchTab("companies")}
          >
            <i className="material-icons">business</i>
            Companies
          </button>
          <AiSearchBox />
        </div>
        <AiSearchNote />

        <div className={`dsTabContent ${tabFading ? "dsTabContent--fading" : ""}`}>
          {activeTab === "contacts" ? (
            <ContactSearchTable
              setTableLoading={setTableLoading}
              tableLoading={tableLoading}
            />
          ) : (
            <CompanySearchTable />
          )}
        </div>
      </div>
      <CreateEditListPopup />
      <PopupLoader />
      {showIcpPopup && (
        <SellerUrlPromptPopup
          onClose={() => setShowIcpPopup(false)}
          onProceed={draft => {
            setShowIcpPopup(false);
            history.push({
              pathname: "/settings/seller-profiles/new",
              state: { draft, returnTo: "/list-builder" }
            });
          }}
        />
      )}
    </section>
  );
});

export { DataSearch };
export default DataSearch;
